import { nextTick, onMounted, onUnmounted, ref, watch } from 'vue';
import { repeat } from '@/worker/utils';
import { storeToRefs } from 'pinia';
import { useAuthStore } from '@/common/stores/auth';

// visibility false→true 복귀 시 dispatch.
export const REPEAT_VISIBILITY_RESUME = 'repeat-visibility-resume';

export interface RepeatOptions {
  isSetup?: boolean;
  isImmediate?: boolean;
  visibilityResumeTarget?: EventTarget;
}

export const useRepeat = (
  executeApi: () => void,
  timeout: number,
  options?: RepeatOptions,
  abortApi?: () => void,
) => {
  const { isCallable, tokenInfo } = storeToRefs(useAuthStore());

  const isRunningImmediate = ref<boolean>(false);
  const { isSetup = true, isImmediate = false, visibilityResumeTarget } = options ?? {};
  let isRunning = false;

  const fetch = {
    timer: 0,
    timeout,
  };

  const clearFetch = async ({ immediate } = { immediate: false }) => {
    if (!immediate) {
      await nextTick();
    }

    clearTimeout(fetch.timer);

    if (abortApi && typeof abortApi === 'function') {
      abortApi();
    }

    isRunning = false;
  };

  const resetFetch = (
    resetOptions?: { fn?: () => void; timeout?: number },
    newTimeout?: number,
    isExecute = false,
  ) => {
    if (!isRunningImmediate.value || isExecute) {
      fetch.timeout = newTimeout || resetOptions?.timeout || fetch.timeout;
      clearTimeout(fetch.timer);
      repeat(resetOptions?.fn || executeApi, fetch);
      isRunning = true;
    }
    isRunningImmediate.value = false;
  };

  if (isSetup) {
    if (isImmediate) {
      isRunningImmediate.value = true;
      onMounted(() => {
        repeat(executeApi, fetch);
        isRunning = true;
      });
    }

    onUnmounted(() => {
      isRunningImmediate.value = false;
      isRunning = false;
      clearFetch();
    });
  } else if (isImmediate) {
    isRunningImmediate.value = true;
    isRunning = true;
    clearTimeout(fetch.timer);
    repeat(executeApi, fetch);
  }

  const resume = () => {
    clearTimeout(fetch.timer);
    repeat(executeApi, fetch);
  };

  // visibility false→true 복귀 시 resumeTarget이 있으면 repeat 호출하지 않고 dispatch 한다.
  const resumeWithTarget = () => {
    let dispatched = false;

    if (visibilityResumeTarget) {
      try {
        visibilityResumeTarget.dispatchEvent(new Event(REPEAT_VISIBILITY_RESUME));
        dispatched = true;
      } catch (e) {
        console.warn('visibilityResumeTarget dispatch failed', e);
      }
    }

    if (!dispatched) {
      resume();
    }
  };

  watch([isCallable, tokenInfo], ([isVisible, tokenObj], [prevIsVisible]) => {
    if (isVisible && tokenObj.accessToken && isRunning) {
      if (prevIsVisible === false) {
        resumeWithTarget();
        return;
      }

      resume();
    } else if (!isVisible) {
      // 탭 hidden(또는 token pending) 진입 시 3분 유예 없이 즉시 폴링 체인 정지 (DSP-43279)
      clearTimeout(fetch.timer);
    }
  });

  return { fetch, resetFetch, clearFetch };
};
